import { decorateRow } from "./landmarks.js";
import { getRoadCatalog } from "./roads.js";
import {
  getLeaderboard,
  getProfile,
  hasChosenPilotName,
  normalizePilotName,
  setPilotName,
  startCloudBackup,
  submitScore,
} from "./services.js";

const CONTROLS_KEY = "orbitrush_controls_v1";
const COACH_KEY = "orbitrush_coach_seen_v1";
const LANE_WIDTH = 2.3;
const ROW_LENGTH = 6;
const BLOCK_HEIGHT = 2.3;

const DEFAULT_CONTROLS = {
  left: "ArrowLeft",
  right: "ArrowRight",
  accel: "ArrowUp",
  brake: "ArrowDown",
  jump: "Space",
};

const state = {
  orb: null,
  finishKey: null,
  modal: null,
  modalDismissible: true,
  gamepadIndex: null,
  previousPad: {},
  menuAxisReady: true,
  bindings: loadBindings(),
};

function loadBindings() {
  try {
    return {
      ...DEFAULT_CONTROLS,
      ...JSON.parse(localStorage.getItem(CONTROLS_KEY) || "{}"),
    };
  } catch {
    return { ...DEFAULT_CONTROLS };
  }
}

function saveBindings() {
  localStorage.setItem(CONTROLS_KEY, JSON.stringify(state.bindings));
}

function formatTime(seconds) {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds - minutes * 60;
  return minutes
    ? `${minutes}:${remainder.toFixed(2).padStart(5, "0")}`
    : `${remainder.toFixed(2)}s`;
}

function formatKey(code) {
  return code
    .replace(/^Key/, "")
    .replace(/^Digit/, "")
    .replace("Arrow", "")
    .replace("Space", "SPACE")
    .toUpperCase();
}

function getStars(road, time) {
  if (time <= road.star3) return 3;
  if (time <= road.star2) return 2;
  return 1;
}

function showToast(message, tone = "normal") {
  let toast = document.querySelector(".orbitrush-toast");
  if (!toast) {
    toast = document.createElement("div");
    toast.className = "orbitrush-toast";
    toast.setAttribute("role", "status");
    document.body.append(toast);
  }
  toast.dataset.tone = tone;
  toast.textContent = message;
  toast.classList.add("show");
  clearTimeout(showToast.timeout);
  showToast.timeout = setTimeout(() => toast.classList.remove("show"), 2600);
}

function createModal(title, { dismissible = true } = {}) {
  closeModal(true);
  const backdrop = document.createElement("div");
  backdrop.className = "orbitrush-modal-backdrop";
  backdrop.addEventListener("pointerdown", (event) => {
    if (dismissible && event.target === backdrop) closeModal();
  });

  const modal = document.createElement("section");
  modal.className = "orbitrush-modal";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-labelledby", "orbitrush-modal-title");

  const header = document.createElement("header");
  const heading = document.createElement("h2");
  heading.id = "orbitrush-modal-title";
  heading.textContent = title;
  const close = document.createElement("button");
  close.className = "orbitrush-icon-button";
  close.type = "button";
  close.setAttribute("aria-label", "Close");
  close.title = "Close";
  close.textContent = "X";
  close.addEventListener("click", () => closeModal());
  header.append(heading);
  if (dismissible) header.append(close);

  const content = document.createElement("div");
  content.className = "orbitrush-modal-content";
  modal.append(header, content);
  backdrop.append(modal);
  document.body.append(backdrop);
  state.modal = backdrop;
  state.modalDismissible = dismissible;
  if (dismissible) close.focus();
  return content;
}

function closeModal(force = false) {
  if (state.modal && !state.modalDismissible && !force) return;
  state.modal?.remove();
  state.modal = null;
  state.modalDismissible = true;
}

function addText(parent, className, text) {
  const element = document.createElement("div");
  element.className = className;
  element.textContent = text;
  parent.append(element);
  return element;
}

function openRoadDetails(road) {
  const content = createModal(road.name);
  addText(content, "orbitrush-road-description", road.desc);

  const targets = document.createElement("div");
  targets.className = "orbitrush-target-grid";
  for (const [stars, value] of [
    [3, road.star3],
    [2, road.star2],
    [1, null],
  ]) {
    const target = document.createElement("div");
    addText(target, "orbitrush-target-stars", `${"★".repeat(stars)}${"☆".repeat(3 - stars)}`);
    addText(
      target,
      "orbitrush-target-time",
      value ? `UNDER ${formatTime(value)}` : "COMPLETE THE ROAD",
    );
    targets.append(target);
  }
  content.append(targets);

  const status = addText(content, "orbitrush-service-status", "Loading global times...");
  const list = document.createElement("div");
  list.className = "orbitrush-board-list";
  content.append(list);

  getLeaderboard(road.id).then((result) => {
    status.textContent = result.online
      ? result.calibration
        ? `${result.sampleCount} verified runs · targets under review`
        : "GLOBAL LEADERBOARD"
      : "OFFLINE TIMES";
    renderBoard(list, result.entries);
  });
}

function renderBoard(container, entries) {
  container.replaceChildren();
  if (!entries?.length) {
    addText(container, "orbitrush-board-empty", "No recorded runs yet. Set the first time.");
    return;
  }
  entries.slice(0, 8).forEach((entry, index) => {
    const row = document.createElement("div");
    row.className = "orbitrush-board-row";
    if (entry.developer) {
      row.classList.add("developer-benchmark");
      row.title = "Developer benchmark time";
    }
    addText(row, "orbitrush-board-rank", `#${index + 1}`);
    addText(row, "orbitrush-board-name", entry.name || "LOCAL PILOT");
    addText(row, "orbitrush-board-time", formatTime(Number(entry.time)));
    container.append(row);
  });
}

function enhanceRoadRows() {
  const catalog = Object.values(getRoadCatalog());
  for (const row of document.querySelectorAll(".road-row:not([data-orbitrush-a11y])")) {
    row.dataset.orbitrushA11y = "true";
    const name = row.querySelector(".road-name")?.textContent?.trim();
    const road = catalog.find((entry) => entry.name === name);
    const locked =
      row.classList.contains("road-locked") ||
      row.closest(".world-card")?.classList.contains("locked");

    if (!locked) {
      row.tabIndex = 0;
      row.setAttribute("role", "button");
      row.setAttribute("aria-label", `Play ${name}`);
      row.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          row.click();
        } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          const rows = [
            ...document.querySelectorAll('.road-row[tabindex="0"]'),
          ];
          const current = rows.indexOf(row);
          const delta = event.key === "ArrowDown" ? 1 : -1;
          rows[(current + delta + rows.length) % rows.length]?.focus();
        }
      });
    }

    if (!road || locked) continue;
    const info = document.createElement("button");
    info.type = "button";
    info.className = "orbitrush-road-info";
    info.textContent = "i";
    info.title = `Times and leaderboard for ${road.name}`;
    info.setAttribute("aria-label", `Times and leaderboard for ${road.name}`);
    info.addEventListener("pointerdown", (event) => event.stopPropagation());
    info.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      openRoadDetails(road);
    });
    row.append(info);
  }
}

function openControls() {
  const content = createModal("CONTROLS");
  addText(
    content,
    "orbitrush-modal-copy",
    "Select an action, then press the key you want to use.",
  );
  const list = document.createElement("div");
  list.className = "orbitrush-control-list";
  content.append(list);

  const render = () => {
    list.replaceChildren();
    for (const [action, label] of [
      ["left", "STEER LEFT"],
      ["right", "STEER RIGHT"],
      ["accel", "ACCELERATE"],
      ["brake", "BRAKE"],
      ["jump", "JUMP"],
    ]) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "orbitrush-control-row";
      addText(button, "orbitrush-control-label", label);
      addText(button, "orbitrush-keycap", formatKey(state.bindings[action]));
      button.addEventListener("click", () => {
        button.classList.add("listening");
        button.querySelector(".orbitrush-keycap").textContent = "PRESS KEY";
        const listener = (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          state.bindings[action] = event.code;
          saveBindings();
          window.removeEventListener("keydown", listener, true);
          render();
        };
        window.addEventListener("keydown", listener, {
          capture: true,
          once: true,
        });
      });
      list.append(button);
    }
  };
  render();

  const reset = document.createElement("button");
  reset.type = "button";
  reset.className = "orbitrush-secondary-button";
  reset.textContent = "RESET DEFAULTS";
  reset.addEventListener("click", () => {
    state.bindings = { ...DEFAULT_CONTROLS };
    saveBindings();
    render();
  });
  content.append(reset);
}

function openPilotProfile({ onboarding = false } = {}) {
  const profile = getProfile();
  const content = createModal(
    onboarding ? "CHOOSE YOUR CALLSIGN" : "PILOT PROFILE",
    { dismissible: !onboarding },
  );
  addText(
    content,
    "orbitrush-modal-copy",
    onboarding
      ? "Choose the name that will appear beside your leaderboard times."
      : "This callsign appears beside your shared leaderboard times.",
  );
  const form = document.createElement("form");
  form.className = "orbitrush-profile-form";
  const input = document.createElement("input");
  input.minLength = 2;
  input.maxLength = 16;
  input.value = profile.name;
  input.setAttribute("aria-label", "Pilot callsign");
  input.autocomplete = "off";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.textContent = onboarding ? "START" : "SAVE CALLSIGN";
  form.append(input, submit);
  const error = addText(content, "orbitrush-profile-error", "");
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const cleanName = normalizePilotName(input.value);
    if (cleanName.length < 2) {
      error.textContent = "ENTER AT LEAST 2 LETTERS OR NUMBERS";
      input.focus();
      return;
    }
    const updated = setPilotName(cleanName);
    showToast(`PILOT ${updated.name} READY`, "success");
    closeModal(true);
  });
  error.before(form);
  input.focus();
  input.select();
}

function showPilotOnboarding() {
  if (hasChosenPilotName()) return;
  openPilotProfile({ onboarding: true });
}

function enhanceSettings() {
  const list = document.querySelector(".settings-panel .settings-list");
  if (!list || list.dataset.orbitrushEnhanced) return;
  list.dataset.orbitrushEnhanced = "true";

  const controls = document.createElement("button");
  controls.className = "setting-row2";
  controls.dataset.nav = "true";
  addText(controls, "set-label", "CONTROLS");
  addText(controls, "set-ctrl orbit-ctrl-value", "CUSTOMISE");
  controls.addEventListener("click", openControls);

  const profile = document.createElement("button");
  profile.className = "setting-row2";
  profile.dataset.nav = "true";
  addText(profile, "set-label", "PILOT CALLSIGN");
  addText(profile, "set-ctrl orbit-ctrl-value", getProfile().name);
  profile.addEventListener("click", openPilotProfile);
  list.append(controls, profile);
}

function renderFinishLeaderboard(panel, result) {
  let board = panel.querySelector(".orbitrush-finish-board");
  if (!board) {
    board = document.createElement("section");
    board.className = "orbitrush-finish-board";
    const heading = document.createElement("div");
    heading.className = "orbitrush-finish-board-title";
    board.append(heading, document.createElement("div"));
    const targets = panel.querySelector(".finish-targets")?.parentElement;
    targets?.after(board);
  }
  if (!board) return;
  board.querySelector(".orbitrush-finish-board-title").textContent = result.online
    ? `GLOBAL TIMES${result.rank ? ` · YOU #${result.rank}` : ""}`
    : "OFFLINE TIMES";
  renderBoard(board.lastElementChild, result.entries);
}

async function enhanceFinishScreen() {
  const title = document.querySelector(".finish-title");
  const panel = title?.closest(".menu-panel");
  const orb = state.orb;
  if (!panel || !orb?.road || !orb.finished) return;
  const runTime = Number(orb.runTime);
  const finishKey = `${orb.road.id}:${runTime.toFixed(3)}`;
  if (state.finishKey === finishKey) return;
  state.finishKey = finishKey;

  const stars = getStars(orb.road, runTime);
  const loading = {
    online: false,
    entries: [],
  };
  renderFinishLeaderboard(panel, loading);
  const result = await submitScore({
    roadId: orb.road.id,
    time: runTime,
    stars,
  });
  renderFinishLeaderboard(panel, result);
}

function showCoach() {
  if (localStorage.getItem(COACH_KEY)) return;
  const orb = state.orb;
  if (!orb || orb.phase !== "playing") return;
  localStorage.setItem(COACH_KEY, "1");

  const coach = document.createElement("aside");
  coach.className = "orbitrush-coach";
  coach.setAttribute("role", "status");
  const mobile = matchMedia("(pointer: coarse)").matches;
  addText(coach, "orbitrush-coach-kicker", "FIRST FLIGHT");
  addText(
    coach,
    "orbitrush-coach-copy",
    mobile
      ? "Hold thrust to build speed. Steer with the left pad and tap JUMP before gaps."
      : "Hold ↑ to build speed. Steer with ← → and press SPACE before gaps.",
  );
  addText(
    coach,
    "orbitrush-coach-copy small",
    "Coloured tiles only affect you while grounded. Reach the portal to finish.",
  );
  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.textContent = "GOT IT";
  dismiss.addEventListener("click", () => coach.remove());
  coach.append(dismiss);
  document.body.append(coach);
  setTimeout(() => coach.remove(), 10_000);
}

function patchEngine(orb) {
  if (orb.__orbitrushPatched) return;
  orb.__orbitrushPatched = true;
  state.orb = orb;

  const originalBuildRowMesh = orb.buildRowMesh.bind(orb);
  const originalAttachEngineGlow = orb.attachEngineGlow.bind(orb);

  orb.cellsFromDef = (definition) => {
    const elevation = Number(definition._elevation || 0);
    const cells = definition.map((type) =>
      type
        ? {
            h: (type === "block" ? BLOCK_HEIGHT : 0) + elevation,
            type,
          }
        : null,
    );
    for (const [key, value] of Object.entries(definition)) {
      if (!/^\d+$/.test(key)) cells[key] = value;
    }
    return cells;
  };

  orb.buildRowMesh = (rowIndex, cells) => {
    const result = originalBuildRowMesh(rowIndex, cells);
    result.group.position.x = Number(cells._offset || 0);
    result.group.position.y = Number(cells._elevation || 0);
    decorateRow(rowIndex, cells, result.group);
    return result;
  };

  orb.cellAt = (z, x) => {
    const rowIdx = Math.floor(-z / ROW_LENGTH);
    const row = orb.rows.get(rowIdx);
    const offset = Number(row?.cells?._offset || 0);
    const col = Math.round((x - offset) / LANE_WIDTH + 2);
    return !row || col < 0 || col >= 5
      ? { cell: null, rowIdx, col }
      : { cell: row.cells[col], rowIdx, col };
  };

  orb.attachEngineGlow = () => {
    originalAttachEngineGlow();
    orb.engineGlow?.scale.setScalar(1.05);
  };
  orb.engineGlow?.scale.setScalar(1.05);
  if (orb.renderer) orb.renderer.toneMappingExposure = 0.98;
  if (orb.bloom) {
    orb.bloom.strength = 0.58;
    orb.bloom.radius = 0.32;
    orb.bloom.threshold = 0.74;
  }

  showToast("ORBITRUSH UPGRADE SYSTEMS ONLINE", "success");
}

function findAndPatchEngine() {
  if (window.__orb && !window.__orb.__orbitrushPatched) {
    patchEngine(window.__orb);
  } else if (window.__orb) {
    state.orb = window.__orb;
  }
}

function handleCustomKeyboard(event, pressed) {
  const orb = state.orb;
  if (!orb || orb.phase !== "playing" || orb.paused || state.modal) return;
  const action = Object.entries(state.bindings).find(
    ([, code]) => code === event.code,
  )?.[0];
  if (!action) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  orb.setTouch(action, pressed);
}

function navItems() {
  return [
    ...document.querySelectorAll(
      '[data-nav="true"]:not([disabled]), .road-row[tabindex="0"]',
    ),
  ].filter((element) => element.offsetParent !== null);
}

function moveMenuFocus(direction) {
  const items = navItems();
  if (!items.length) return;
  const current = items.indexOf(document.activeElement);
  const next =
    current < 0
      ? 0
      : (current + direction + items.length) % items.length;
  items[next].focus();
  items[next].scrollIntoView({ block: "nearest", behavior: "smooth" });
}

function dispatchEscape() {
  window.dispatchEvent(
    new KeyboardEvent("keydown", {
      key: "Escape",
      code: "Escape",
      bubbles: true,
    }),
  );
}

function pollGamepad() {
  const pads = navigator.getGamepads?.() || [];
  let pad =
    state.gamepadIndex === null ? null : pads[state.gamepadIndex] || null;
  if (!pad) {
    pad = [...pads].find(Boolean) || null;
    state.gamepadIndex = pad?.index ?? null;
  }

  if (pad) {
    const pressed = (index) => Boolean(pad.buttons[index]?.pressed);
    const axisX = pad.axes[0] || 0;
    const axisY = pad.axes[1] || 0;
    const controls = {
      left: pressed(14) || axisX < -0.28,
      right: pressed(15) || axisX > 0.28,
      accel: pressed(7) || axisY < -0.45,
      brake: pressed(6) || axisY > 0.45,
      jump: pressed(0),
      pause: pressed(9),
      back: pressed(1),
    };

    const orb = state.orb;
    if (orb?.phase === "playing" && !orb.paused && !state.modal) {
      for (const action of ["left", "right", "accel", "brake", "jump"]) {
        if (controls[action] !== state.previousPad[action]) {
          orb.setTouch(action, controls[action]);
        }
      }
    } else {
      const vertical = axisY < -0.55 || pressed(12) ? -1 : axisY > 0.55 || pressed(13) ? 1 : 0;
      if (vertical && state.menuAxisReady) {
        moveMenuFocus(vertical);
        state.menuAxisReady = false;
      } else if (!vertical) {
        state.menuAxisReady = true;
      }
      if (controls.jump && !state.previousPad.jump) {
        document.activeElement?.click?.();
      }
    }

    if (controls.pause && !state.previousPad.pause) dispatchEscape();
    if (controls.back && !state.previousPad.back) {
      if (state.modal) closeModal();
      else dispatchEscape();
    }
    state.previousPad = controls;
  }
  requestAnimationFrame(pollGamepad);
}

function observeUi() {
  const observer = new MutationObserver(() => {
    findAndPatchEngine();
    enhanceRoadRows();
    enhanceSettings();
    enhanceFinishScreen();
    showCoach();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

export function installRuntime() {
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && state.modal) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeModal();
      return;
    }
    handleCustomKeyboard(event, true);
  }, true);
  window.addEventListener("keyup", (event) => handleCustomKeyboard(event, false), true);

  window.addEventListener("gamepadconnected", (event) => {
    state.gamepadIndex = event.gamepad.index;
    state.previousPad = {};
    showToast(`CONTROLLER CONNECTED · ${event.gamepad.id.slice(0, 28)}`, "success");
  });
  window.addEventListener("gamepaddisconnected", () => {
    state.gamepadIndex = null;
    state.previousPad = {};
    showToast("CONTROLLER DISCONNECTED");
  });

  findAndPatchEngine();
  observeUi();
  pollGamepad();
  startCloudBackup(() => {
    showToast("CLOUD SAVE RESTORED · RELOADING", "success");
    setTimeout(() => location.reload(), 900);
  });
  requestAnimationFrame(showPilotOnboarding);
}
